/**
 * Playground Container - Main Layout Component
 * Enterprise-grade component with proper architecture patterns, security, and performance
 */

import { Suspense } from 'react';
import { ErrorBoundary } from '@/components/common/error-boundary/ErrorBoundary';
import { LoadingSpinner } from '../shared/components/LoadingScreen';
import { Header } from '@/components/layout/header/Header';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  RefreshCw,
  Zap,
  Sparkles,
  Shield,
  Github,
  ExternalLink,
} from 'lucide-react';
import { usePlaygroundState } from '../shared/hooks/usePlaygroundState';
import { cn } from '@/lib/utils';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface PlaygroundContainerProps {
  children?: React.ReactNode;
  className?: string;
  enableKeyboardShortcuts?: boolean;
}

// ============================================================================
// Error Handling Components
// ============================================================================

function PlaygroundErrorFallback({
  error,
  resetError,
}: {
  error?: Error;
  resetError: () => void;
}) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 text-center border-destructive/20 bg-background">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-2 text-foreground">
          Playground Error
        </h2>

        <p className="text-sm text-muted-foreground mb-4">
          An unexpected error occurred in the unified playground. This error has
          been logged for investigation.
        </p>

        {error && (
          <details className="mb-4 text-left">
            <summary className="text-sm font-medium cursor-pointer hover:text-foreground transition-colors">
              Error Details
            </summary>
            <pre className="mt-2 text-xs bg-muted p-2 rounded text-muted-foreground overflow-auto">
              {error.message}
            </pre>
          </details>
        )}

        <Button
          onClick={resetError}
          className="transition-all duration-200 hover:scale-105"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </Card>
    </div>
  );
}

// ============================================================================
// Sub-Components
// ============================================================================

// Performance-aware loading fallback
function PlaygroundSuspenseFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center animate-fadeInUp">
        <LoadingSpinner size="lg" className="mb-4" />
        <p className="text-sm text-muted-foreground">
          Initializing secure playground...
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Playground Container Component
// ============================================================================

export function PlaygroundContainer({
  children,
  className,
}: PlaygroundContainerProps) {
  // State management with custom hooks
  const { hasAvailableApis, availableApiCount } = usePlaygroundState();

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <ErrorBoundary fallback={PlaygroundErrorFallback}>
      <div
        className={cn(
          'min-h-screen bg-background transition-colors duration-200',
          className,
        )}
      >
        {/* Unified Header Component */}
        <Header />

        {/* Main Content */}
        <main className="container mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-screen-2xl">
          <Suspense fallback={<PlaygroundSuspenseFallback />}>
            <div className="animate-fadeInUp">
              {children || (
                <div className="text-center py-16">
                  <div className="max-w-2xl mx-auto">
                    <div className="w-20 h-20 mx-auto mb-10 rounded-full bg-gradient-to-r from-blue-500 via-purple-600 to-green-500 flex items-center justify-center animate-aiPulse">
                      <Zap className="w-10 h-10 text-white" />
                    </div>

                    <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
                      Welcome to Chrome AI DevBench
                    </h2>

                    <p className="text-lg text-muted-foreground mb-4">
                      Enterprise-grade playground for Chrome&apos;s built-in AI
                      APIs. Secure, performant, and production-ready.
                    </p>

                    {/* Status Summary */}
                    <div className="mb-8 flex items-center justify-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span>Secure</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-600" />
                        <span>{availableApiCount}/7 APIs Ready</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      <Card className="p-4 hover-lift transition-all duration-200 cursor-pointer border-green-200 dark:border-green-800">
                        <h3 className="font-semibold mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-600" />
                          Security First
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Input validation, XSS protection, and enterprise
                          security standards.
                        </p>
                      </Card>

                      <Card className="p-4 hover-lift transition-all duration-200 cursor-pointer border-purple-200 dark:border-purple-800">
                        <h3 className="font-semibold mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-purple-600" />
                          Production Ready
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          TypeScript, error boundaries, and enterprise-grade
                          architecture.
                        </p>
                      </Card>
                    </div>

                    <Button
                      size="lg"
                      className="transition-all duration-200 hover:scale-105 hover-glow"
                      disabled={!hasAvailableApis}
                    >
                      {hasAvailableApis
                        ? 'Start Exploring APIs'
                        : 'APIs Unavailable'}
                      <Sparkles className="w-4 h-4 ml-2" />
                    </Button>

                    {!hasAvailableApis && (
                      <p className="text-sm text-muted-foreground mt-4">
                        Requires Chrome 138+ with experimental AI flags enabled.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Suspense>
        </main>

        {/* Footer */}
        <footer className="border-t border-border/50 bg-muted/20 mt-20">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center gap-4 mb-4 md:mb-0">
                <p className="text-sm text-muted-foreground">
                  Built with enterprise security & performance standards
                </p>
              </div>

              <div className="flex items-center gap-4">
                <a
                  href="https://github.com/abodacs/build-in-ai-2025"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Github className="w-4 h-4" />
                  <span>GitHub</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://developer.chrome.com/docs/ai/built-in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>Documentation</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

// ============================================================================
// Compound Component Pattern Export
// ============================================================================

export const Playground = {
  Container: PlaygroundContainer,
  ErrorFallback: PlaygroundErrorFallback,
  SuspenseFallback: PlaygroundSuspenseFallback,
} as const;
